import { useCallback, useEffect, useRef } from "react";
import { usePostApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  setAutoSaving,
  setCurrent,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";

import { generatorStore } from "../../store";
import { getCurrentUTC } from "../../../utils/timeUtils/getCurrentUTC";
import { useEntryNavigation } from "./useEntryNavigation";
import { useEntryParams } from "./useEntryParams";

interface UseAutosaveOptions {
  enableAutosave?: boolean;
  autosaveDelayMs?: number;
}

/**
 * V2 Autosave - triggers after 5s of inactivity, then releases lock
 */
export const useAutosave = (
  params: ReturnType<typeof useEntryParams>,
  navigation: ReturnType<typeof useEntryNavigation>,
  options: UseAutosaveOptions = {},
) => {
  const { enableAutosave = false } = options;
  const { post } = usePostApi();
  const dispatch = useDispatch();

  const isDirtyRef = useRef(false);

  const {
    getCommonParams,
    getRouteEntryId,
    current,
    entryIdType,
    isReadOnly,
    isAutoSaving,
    isNewEntry,
    editing,
    isDirty,
  } = params;

  const openedSaveModal = useSelector(
    state => state.entrySlice.openedSaveModal,
  );

  // Track previous editing state
  const prevEditingRef = useRef(editing);

  const { navigateToSavedEntry } = navigation;

  const autoSaveExistingEntry = useCallback(async () => {
    const baseParams = getCommonParams();
    const entryId = getRouteEntryId();
    if (!baseParams || !entryId) {
      dispatch(setReleaseEntryLock(Math.random()));
      return;
    }

    dispatch(setAutoSaving({ isSaving: true }));
    try {
      const result = await post({
        request: "auto-save-entry",
        body: {
          params: {
            _current: current,
            autosave: true,
            ...baseParams,
            [entryIdType]: entryId,
          },
        },
      });

      dispatch(setAutoSaving({ isSaving: false }));

      if (result) {
        dispatch(setCurrent(result.current || getCurrentUTC()));
        // On successful save, clear the dirty flag
        generatorStore.getState().setDirty(false);
      }

      // Always release lock after autosave attempt (success or fail)
      dispatch(setReleaseEntryLock(Math.random()));
    } catch {
      dispatch(setAutoSaving({ isSaving: false, isError: true }));
      // Release lock even on error
      dispatch(setReleaseEntryLock(Math.random()));
    }
  }, [current, dispatch, entryIdType, getCommonParams, getRouteEntryId, post]);

  const autoSaveNewEntry = useCallback(async () => {
    const baseParams = getCommonParams();
    if (!baseParams) {
      dispatch(setReleaseEntryLock(Math.random()));
      return;
    }

    dispatch(setAutoSaving({ isSaving: true }));
    try {
      const result = await post({
        request: "draft-save-entry",
        body: {
          params: {
            autosave: true,
            ...baseParams,
            [entryIdType]: "new",
          },
        },
      });

      dispatch(setAutoSaving({ isSaving: false }));

      if (result) {
        dispatch(setCurrent(result.current || getCurrentUTC()));
        // On successful save, clear the dirty flag
        generatorStore.getState().setDirty(false);
        navigateToSavedEntry(result);
      }

      // Always release lock after autosave attempt
      dispatch(setReleaseEntryLock(Math.random()));
    } catch {
      dispatch(setAutoSaving({ isSaving: false, isError: true }));
      // Release lock even on error
      dispatch(setReleaseEntryLock(Math.random()));
    }
  }, [dispatch, entryIdType, getCommonParams, navigateToSavedEntry, post]);

  // Autosave trigger
  const performAutosave = useCallback(async () => {
    if (isReadOnly || isAutoSaving) {
      // If already saving (e.g. manual save in progress),
      // let that save process release the lock.
      return;
    }

    if (isNewEntry) {
      await autoSaveNewEntry();
    } else {
      await autoSaveExistingEntry();
    }
  }, [
    autoSaveExistingEntry,
    autoSaveNewEntry,
    isAutoSaving,
    isNewEntry,
    isReadOnly,
  ]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  // Inactivity-based autosave
  useEffect(() => {
    if (!enableAutosave || isReadOnly || openedSaveModal) {
      prevEditingRef.current = editing;
      return;
    }

    // Since store.ts waits 5s before setting editing=false,
    // we can just fire immediately when editing becomes false
    if (!editing && prevEditingRef.current) {
      if (isDirtyRef.current) {
        void performAutosave();
      } else {
        // If it's not dirty, no save is needed. Release lock immediately.
        dispatch(setReleaseEntryLock(Math.random()));
      }
    }

    // Always update previous state tracking at end of effect
    prevEditingRef.current = editing;
  }, [
    enableAutosave,
    isReadOnly,
    openedSaveModal,
    editing,
    performAutosave,
    dispatch,
  ]);

  // No cleanup timer left, just return the functions
  return { autoSave: performAutosave, isSaving: isAutoSaving };
};
